// src/pages/AdminUrlTrace.tsx — #3 URL/Tag izleme (admin, salt-okuma)
import React, { useState, useEffect, useCallback } from 'react'
import { apiService } from '../services/api'
import type { UrlTraceEventRow } from '../services/api'
import { Loader2, AlertTriangle, ChevronLeft, ChevronRight, RefreshCw, Trash2, CheckSquare, Square } from 'lucide-react'
import toast from 'react-hot-toast'

const stepLabel: Record<string, string> = {
  dp: 'Ürün (DP)', cart: 'Sepet', checkout: 'Ödeme', thankyou: 'Tamamlandı', other: 'Diğer'
}

export default function AdminUrlTrace() {
  const [rows, setRows] = useState<UrlTraceEventRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [onlyForeign, setOnlyForeign] = useState(true)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [deleting, setDeleting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await apiService.getAdminUrlTrace({ page, pageSize, onlyForeign })
      setRows(r.results || [])
      setTotal(r.total || 0)
      setSelected(new Set())
    } catch (e: any) {
      toast.error(e?.message || 'Yüklenemedi')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, onlyForeign])

  useEffect(() => { load() }, [load])

  const toggleSel = (id: string) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n
  })
  const allOnPageSelected = rows.length > 0 && rows.every(r => selected.has(r.id))
  const toggleAllOnPage = () => setSelected(prev => {
    const n = new Set(prev)
    if (allOnPageSelected) rows.forEach(r => n.delete(r.id))
    else rows.forEach(r => n.add(r.id))
    return n
  })
  const deleteOne = async (id: string) => {
    if (!window.confirm('Bu kaydı silmek istiyor musun?')) return
    try { await apiService.deleteUrlTrace(id); toast.success('Silindi'); load() }
    catch (e: any) { toast.error(e?.message || 'Silinemedi') }
  }
  const deleteSelected = async () => {
    if (selected.size === 0) return
    if (!window.confirm(`${selected.size} kayıt silinecek. Emin misin?`)) return
    setDeleting(true)
    try {
      const r = await apiService.bulkDeleteUrlTrace({ ids: Array.from(selected) })
      toast.success(`${r.deleted} kayıt silindi`); load()
    } catch (e: any) { toast.error(e?.message || 'Silinemedi') }
    finally { setDeleting(false) }
  }
  const deleteAll = async () => {
    if (!window.confirm(onlyForeign
      ? 'TÜM yabancı-tag kayıtları silinecek. Emin misin?'
      : 'TÜM izleme kayıtları silinecek. Emin misin?')) return
    setDeleting(true)
    try {
      const r = await apiService.bulkDeleteUrlTrace({ all: true, onlyForeign })
      toast.success(`${r.deleted} kayıt silindi`); setPage(1); load()
    } catch (e: any) { toast.error(e?.message || 'Silinemedi') }
    finally { setDeleting(false) }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const fmt = (s: string) => new Date(s).toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit'
  })

  return (
    <div className="space-y-4">
      {/* Bilgi + filtre */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
        <p className="text-sm text-amber-800">
          Sipariş akışında görülen affiliate tag'ler <strong>pasif</strong> olarak loglanır (müdahale yok).
          <strong> Kırmızı ⚠ = bizim havuzumuzda olmayan yabancı tag</strong> — muhtemel hijack/rakip eklenti.
        </p>
      </div>

      <div className="bg-white rounded-xl p-4 shadow-sm flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700">
          <input type="checkbox" checked={onlyForeign}
            onChange={e => { setPage(1); setOnlyForeign(e.target.checked) }}
            className="w-4 h-4 rounded border-gray-300" />
          Sadece yabancı tag
        </label>
        <span className="text-sm text-gray-500">Toplam: {total}</span>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={deleteSelected}
            disabled={selected.size === 0 || deleting}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-4 h-4" /> Seçilenleri sil{selected.size > 0 ? ` (${selected.size})` : ''}
          </button>
          <button
            onClick={deleteAll}
            disabled={deleting || total === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-4 h-4" /> {onlyForeign ? 'Tüm yabancıları sil' : 'Tümünü sil'}
          </button>
          <button onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">Kayıt yok.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase">
                  <th className="px-4 py-3">
                    <button onClick={toggleAllOnPage} title="Sayfadakileri seç/kaldır" className="p-0.5">
                      {allOnPageSelected ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4 text-gray-400" />}
                    </button>
                  </th>
                  <th className="px-4 py-3">Zaman</th>
                  <th className="px-4 py-3">Kullanıcı</th>
                  <th className="px-4 py-3">Adım</th>
                  <th className="px-4 py-3">Sipariş No</th>
                  <th className="px-4 py-3">Bizim Tag</th>
                  <th className="px-4 py-3">Görülen Tag</th>
                  <th className="px-4 py-3">Komisyon</th>
                  <th className="px-4 py-3">ASIN</th>
                  <th className="px-4 py-3">URL</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(r => (
                  <tr key={r.id} className={`${r.isForeignTag ? 'bg-red-50' : ''} ${selected.has(r.id) ? 'bg-blue-50' : ''}`}>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleSel(r.id)} className="p-0.5">
                        {selected.has(r.id) ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4 text-gray-400" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmt(r.createdAtUtc)}</td>
                    <td className="px-4 py-3 text-gray-700">{r.email}</td>
                    <td className="px-4 py-3 text-gray-600">{stepLabel[r.step || 'other'] || r.step}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{r.orderId || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">
                      {r.orderAffiliateTag || r.expectedTag || '—'}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {r.isForeignTag ? (
                        <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                          <AlertTriangle className="w-3.5 h-3.5" /> {r.observedTag}
                        </span>
                      ) : (
                        <span className="text-gray-600">{r.observedTag || '—'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{r.commissionRate != null ? `%${r.commissionRate}` : '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{r.asin || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-400 max-w-[280px] truncate" title={r.url || ''}>
                      {r.url || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => deleteOne(r.id)} title="Sil" className="p-1 text-red-500 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
            <span className="text-sm text-gray-600">Sayfa {page} / {totalPages}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
